// FP3-115 — Visual grade-constraint overlay for the Schedule Builder.
//
// Renders subtle amber hatched rectangles behind existing schedule blocks
// showing when students in a given grade cannot be scheduled because of
// existing School Rules / grade constraints. This is a VISUAL AID ONLY —
// no data is created, no drop is blocked here (the existing Builder
// validation still catches illegal placements), and overlay rects are
// never included in Save payloads or unsaved-change state.
//
// Canonical source: the same `grade_schedules` collection the Wizard
// consumes (see backend/routes/wizard.py `_wizard_context` grade-rule
// loading at ~L1832 and the Builder's own `findGradeConstraintConflict`
// helper). This module never touches the DB or state — it takes the
// already-fetched `gradeSchedules` list and derives visual rects from it.
import { pct, toMin } from "./utils";

/**
 * Resolve visual overlay rectangles for a given (school, grade, pattern).
 *
 * Mirrors the wizard's grade-rule loader:
 *   - `day_of_week === -1` expands to Mon..Fri (matches wizard behavior)
 *   - `day_of_week 0..4` renders only on that weekday
 *   - `end_time <= start_time` is skipped defensively
 *   - malformed times are skipped defensively
 *
 * Returns: flat list of
 *   { id, constraintId, day, startMin, endMin, kind, label, grade }
 * ready for direct placement in the drop-zone via `pct()`.
 */
export function resolveGradeConstraintsForOverlay({
  gradeSchedules,
  schoolId,
  grade,
  pattern,
}) {
  if (!schoolId) return [];
  const gradeStr = String(grade ?? "").trim();
  if (!gradeStr) return [];
  const pat = pattern || "full_day";
  const gs = (gradeSchedules || []).find(
    (g) =>
      g.school_id === schoolId &&
      String(g.grade ?? "").trim() === gradeStr,
  );
  if (!gs) return [];
  const slot = (gs.patterns || {})[pat];
  if (!slot) return [];
  const constraints = Array.isArray(slot.constraints) ? slot.constraints : [];
  const out = [];
  constraints.forEach((c, cIdx) => {
    let sm;
    let em;
    try {
      sm = toMin(c.start_time);
      em = toMin(c.end_time);
    } catch {
      return;
    }
    if (!Number.isFinite(sm) || !Number.isFinite(em) || em <= sm) return;
    const dow = typeof c.day_of_week === "number" ? c.day_of_week : -1;
    const days = dow === -1 ? [0, 1, 2, 3, 4] : [dow];
    days.forEach((d) => {
      if (d < 0 || d > 4) return;
      out.push({
        id: `${c.id || cIdx}-${d}`,
        constraintId: c.id || `idx-${cIdx}`,
        day: d,
        startMin: sm,
        endMin: em,
        kind: (c.kind || "other").toString(),
        label: (c.label || "").toString(),
        grade: gradeStr,
      });
    });
  });
  return out;
}

/**
 * Merge overlapping/adjacent rects of the same day into a single visual
 * region carrying every rule's label. Prevents unreadable stacked
 * overlays when a grade has, say, an "AM-wide" rule and a "lunch" rule
 * that visually collide on the same weekday.
 */
function mergeOverlappingByDay(blocks) {
  const byDay = new Map();
  blocks.forEach((b) => {
    if (!byDay.has(b.day)) byDay.set(b.day, []);
    byDay.get(b.day).push(b);
  });
  const out = [];
  byDay.forEach((arr, day) => {
    const sorted = [...arr].sort((a, b) => a.startMin - b.startMin);
    let cur = null;
    sorted.forEach((b) => {
      if (!cur) {
        cur = {
          day,
          startMin: b.startMin,
          endMin: b.endMin,
          rules: [b],
        };
        return;
      }
      if (b.startMin <= cur.endMin) {
        cur.endMin = Math.max(cur.endMin, b.endMin);
        cur.rules.push(b);
      } else {
        out.push(cur);
        cur = {
          day,
          startMin: b.startMin,
          endMin: b.endMin,
          rules: [b],
        };
      }
    });
    if (cur) out.push(cur);
  });
  return out;
}

function ruleLabel(rule) {
  const label = (rule.label || "").trim();
  const kind = rule.kind
    ? rule.kind.charAt(0).toUpperCase() + rule.kind.slice(1)
    : "Rule";
  if (label) return label;
  return kind;
}

/**
 * Renders the overlay rectangles for one day row.
 *
 * Contract (FP3-115 §6):
 *   - `pointer-events: none` so drag/drop is never intercepted
 *   - `zIndex: 0` sitting behind the Builder's placed blocks (which
 *     rely on default auto-z and DOM order)
 *   - never appears in Save payloads (rendered from local overlayBlocks)
 *   - existing service blocks remain visually dominant (low alpha
 *     hatched fill; the amber tint sits well below the calm service
 *     block palette)
 */
export default function GradeConstraintOverlay({
  blocks,
  dayIdx,
  gradeLabel = "",
}) {
  const dayBlocks = (blocks || []).filter((b) => b.day === dayIdx);
  if (dayBlocks.length === 0) return null;
  const merged = mergeOverlappingByDay(dayBlocks);
  return (
    <>
      {merged.map((m) => {
        const left = pct(m.startMin);
        const width = pct(m.endMin) - pct(m.startMin);
        const prefix = gradeLabel ? `G${gradeLabel} · ` : "";
        const primary = ruleLabel(m.rules[0]);
        const extra = m.rules.length > 1 ? ` +${m.rules.length - 1}` : "";
        const compactLabel = `${prefix}${primary}${extra}`;
        const tooltipLines = m.rules.map(
          (r) =>
            `${prefix}${ruleLabel(r)} · ` +
            `${String(Math.floor(r.startMin / 60)).padStart(2, "0")}:` +
            `${String(r.startMin % 60).padStart(2, "0")}–` +
            `${String(Math.floor(r.endMin / 60)).padStart(2, "0")}:` +
            `${String(r.endMin % 60).padStart(2, "0")}`,
        );
        return (
          <div
            key={`grade-overlay-${dayIdx}-${m.rules[0].constraintId}-${m.startMin}`}
            data-testid={`grade-overlay-block-${dayIdx}-${m.rules[0].constraintId}`}
            data-grade-overlay="true"
            aria-hidden="true"
            className="absolute rounded overflow-hidden"
            style={{
              left: `${left}%`,
              width: `${Math.max(0.5, width)}%`,
              top: "4%",
              bottom: "4%",
              // Low-weight amber tint + hatched pattern so it never
              // competes with student service blocks or the reddish
              // provider-unavailable / validation-error styling.
              backgroundColor: "rgba(232, 210, 173, 0.28)",
              backgroundImage:
                "repeating-linear-gradient(135deg, transparent 0 6px, rgba(156, 107, 46, 0.12) 6px 7px)",
              border: "1px dashed rgba(217, 160, 91, 0.65)",
              pointerEvents: "none",
              zIndex: 0,
            }}
            title={tooltipLines.join("\n")}
          >
            <div className="px-1 py-0.5 text-[9px] text-[#7A5220] truncate italic leading-tight">
              {compactLabel}
            </div>
          </div>
        );
      })}
    </>
  );
}
